import clsx from "clsx";

import { useNavigate } from "react-router-dom";

import { BalanceGroup, UserPermissions } from "@/entities/user";
import { PauseButton } from "@/features/PauseButton";
import { MaxWidthWrapper } from "@/shared/ui/MaxWidthWrapper";

import { getNavRoutes } from "../model/nav-routes";
import { MentorInfo } from "@/widgets/MentorInfoModal";
import { ChangeContactsModal } from "@/widgets/ChangeContactsModal";
import { LogoutModal } from "@/widgets/LogoutModal";
import { NotificationsButton } from "@/features/NotificationsButton/NotificationButton";
import { ProfileModal } from "@/widgets/ProfileModal";
import { SendNotificationCard } from "@/widgets/SendNotificationCard/ui/SendNotificationCard";
import { BroadcastToLeadsModal } from "@/widgets/BroadcastToLeadsModal";
import { SwitchThemeButton } from "@/features/SwitchThemeButton";
import { SendNotificationBanner } from "@/widgets/SendNotificationBanner";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { MasterTeamleadModal } from "@/widgets/MasterTeamleadModal";
import { Roles } from "@/entities/user";
import { PhonesExportModal } from "@/widgets/PhoneExportModal";
import { IncreasedRateProgressCard } from "@/features/IncreasedRateProgressCard";
import { TelegramAssignButton } from "@/features/TelegramAssignButton";
import { MentorProfileModal } from "@/features/MentorProfileModal/MentorProfileModal";
import { useSessionStore } from "@/entities/session";
import { ConvertBalancesCard } from "@/widgets/ConvertBalancesCard";
import { CollaborateModal } from "@/widgets/CollaborateModal";

interface SideBarProps {
  isOpen: boolean;
}

export const SideBar = ({ isOpen }: SideBarProps) => {
  const handleNavigate = useNavigate();
  const { user } = useSessionStore();
  const role = user?.role!;
  const permissions = user?.permissions;

  return (
    <div
      className={clsx(
        "fixed flex flex-col max-lg:hidden",
        isOpen ? "translate-x-0" : "-translate-x-full",
        "left-0 top-0 h-[100dvh] max-h-dvh w-[270px] bg-bgColor",
        "transition-transform duration-300",
      )}
    >
      <MaxWidthWrapper className="flex items-center justify-between p-4">
        <ProfileModal user={user} />
        <NotificationsButton />
      </MaxWidthWrapper>

      <hr className="border-[#39393940] dark:border-[#EDEDED26]" />

      <MaxWidthWrapper className="flex flex-1 flex-col py-4">
        <div className="flex flex-1 flex-col gap-1">
          {![
            Roles.Worker,
            Roles.Moderator,
            Roles.Mentor,
            Roles.MasterMentor,
          ].includes(role) && <SwitchThemeButton />}
          {[
            Roles.Worker,
            Roles.Moderator,
            Roles.Mentor,
            Roles.MasterMentor,
            Roles.MasterModerator,
            Roles.Head,
            Roles.TrafficManager,
            Roles.MasterTrafficManager,
          ].includes(role) && (
            <div className="space-y-2">
              <BalanceGroup />
              {(user?.balances?.find((b) => b.asset === "FT_COIN")?.balance ||
                0) > 0 && <ConvertBalancesCard />}

              <CollaborateModal />
            </div>
          )}
          {role === Roles.Worker && <IncreasedRateProgressCard />}

          <TelegramAssignButton />
          {role === Roles.Worker && <MentorInfo />}
          {[Roles.Mentor, Roles.MasterMentor].includes(role) && (
            <MentorProfileModal />
          )}

          <div className="flex flex-1 flex-col overflow-hidden pt-4">
            <div className="flex flex-1 flex-col overflow-hidden">
              <ScrollArea className="h-full" type="auto">
                <ul className="absolute inset-0 flex flex-col gap-4">
                  {getNavRoutes(role || "").map(({ label, link, icon }) => {
                    if (
                      link === "/bots" &&
                      role !== Roles.Head &&
                      !permissions?.includes(UserPermissions.CONSTRUCTOR_BOT)
                    )
                      return;

                    return (
                      <li
                        key={label}
                        className="group flex w-full items-center gap-2 pb-2 hover:cursor-pointer"
                        onClick={() =>
                          label.toLowerCase() === "главная"
                            ? handleNavigate("/")
                            : handleNavigate(link)
                        }
                      >
                        {icon}
                        <span className="bg-gradient-to-r from-gray-500 to-gray-500 bg-[length:0%_2px] bg-left-bottom bg-no-repeat pb-1 text-textColor transition-all duration-500 ease-out group-hover:bg-[length:100%_2px]">
                          {label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </div>
          </div>
        </div>

        <div className="flex max-h-[50%] w-full flex-col gap-4">
          {[Roles.Mentor, Roles.MasterMentor].includes(role) && (
            <>
              <ChangeContactsModal />
              <PauseButton />
            </>
          )}
          {role === Roles.Head && <SendNotificationBanner />}
          {role === Roles.Head && <SendNotificationCard />}
          {role === Roles.Head && <PhonesExportModal />}
          {/* {(role === Roles.Head || permissions?.includes("sms")) && (
            <SMSModal />
          )} */}
          {role === Roles.Head && <BroadcastToLeadsModal />}
          {[
            Roles.Worker,
            Roles.Moderator,
            Roles.Mentor,
            Roles.MasterMentor,
          ].includes(role) && <SwitchThemeButton />}
          {localStorage.getItem("master-teamlead-store") ||
          role === Roles.MasterTeamLead ? (
            <MasterTeamleadModal />
          ) : (
            <div className="flex w-full items-center justify-between">
              <LogoutModal />
              <span className="text-textGrayColor">⌘B</span>
            </div>
          )}
        </div>
      </MaxWidthWrapper>
    </div>
  );
};
